const INIT_UTM_COOKIE = "_sky_init_utm";
const SESSION_UTM_COOKIE = "_sky_session_utm";
const INIT_UTM_EXPIRY_DAYS = 365;
const SESSION_UTM_EXPIRY_DAYS = 1;

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const c = cookies[i].trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }
  return null;
}

function setCookie(name: string, value: string, days: number): void {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie =
    name + "=" + encodeURIComponent(value) + expires + "; path=/; SameSite=Lax";
}

// NOTE: this function is mirrored by an inline <script> in client/index.html.
// The SSG build strips the main SPA bundle from static and island pages, so this
// module only executes on `tier: "spa"` routes — the inline copy is what covers
// the rest of the site. Any change to the cookie names, JSON shape, expiry, or
// the first-touch-never-overwritten rule must be made in BOTH places.
export function captureUtmsToCookies(
  search: string = window.location.search,
): void {
  // Wrapped so attribution can never break the page. main.tsx calls this before
  // createRoot on spa routes — an unguarded throw here would white-screen the
  // app. Mirrors the inline snippet's try/catch in client/index.html.
  try {
    const params = new URLSearchParams(search);
    const current: Record<string, string> = {};
    let hasUtms = false;
    for (const param of UTM_PARAMS) {
      const val = params.get(param);
      if (val) {
        current[param] = val;
        hasUtms = true;
      }
    }
    if (!hasUtms) return;
    if (!getCookie(INIT_UTM_COOKIE)) {
      setCookie(INIT_UTM_COOKIE, JSON.stringify(current), INIT_UTM_EXPIRY_DAYS);
    }
    setCookie(SESSION_UTM_COOKIE, JSON.stringify(current), SESSION_UTM_EXPIRY_DAYS);
  } catch {
    // attribution must never throw
  }
}

function identity(val: string): string {
  return val;
}

// "GCL.<ts>.VALUE" -> "VALUE"  (also used for FPID "FPID2.2.VALUE")
function afterSecondDot(val: string): string {
  const parts = val.split(".");
  return parts.length > 2 ? parts.slice(2).join(".") : val;
}

// "2.1.xVALUE$i<ts>" -> "VALUE"
function gclAgValue(val: string): string {
  const dotIndex = val.lastIndexOf(".");
  const dollarIndex = val.indexOf("$");
  if (dotIndex !== -1 && dollarIndex !== -1 && dollarIndex > dotIndex) {
    return val.substring(dotIndex + 2, dollarIndex);
  }
  return dotIndex !== -1 ? val.substring(dotIndex + 2) : val;
}

interface CookieExtractor {
  // One or more candidate cookies, tried in order — the first one whose
  // extracted value is non-empty wins (a present-but-empty cookie falls
  // through to the next candidate).
  cookie: string | string[];
  key: string;
  extract: (val: string) => string;
}

const COOKIE_EXTRACTORS: CookieExtractor[] = [
  { cookie: "_gcl_aw", key: "gclid", extract: afterSecondDot },
  { cookie: "_gcl_gb", key: "gcl_gb", extract: afterSecondDot },
  { cookie: "_gcl_ag", key: "gcl_ag", extract: gclAgValue },
  // GA4 client id. `_ga` is the standard client-side cookie ("GA1.1.<id>");
  // `FPID` only exists when the server container is configured to write a
  // first-party GA cookie ("FPID2.2.<id>"). Both encode the id after the second
  // dot, so afterSecondDot handles either. Client-side first, server-side as a
  // fallback for setups where `_ga` is unavailable.
  { cookie: ["_ga", "FPID"], key: "ga_client_id", extract: afterSecondDot },
  { cookie: "_my_client_id", key: "my_client_id", extract: identity },
  { cookie: "_my_session_id", key: "my_session_id", extract: identity },
  { cookie: "_fbp", key: "fbp", extract: identity },
  { cookie: "_fbc", key: "fbc", extract: identity },
];

function readUtmCookie(
  cookieName: string,
  prefix: string,
  out: Record<string, string>,
): void {
  const raw = getCookie(cookieName);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    for (const param of UTM_PARAMS) {
      if (parsed[param]) out[prefix + param] = parsed[param];
    }
  } catch {
    // corrupted cookie, ignore
  }
}

export function getTrackingData(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    readUtmCookie(SESSION_UTM_COOKIE, "", out);
    readUtmCookie(INIT_UTM_COOKIE, "init_", out);
    for (const ex of COOKIE_EXTRACTORS) {
      const candidates = Array.isArray(ex.cookie) ? ex.cookie : [ex.cookie];
      for (const name of candidates) {
        const raw = getCookie(name);
        if (!raw) continue;
        const value = ex.extract(raw);
        if (value) {
          out[ex.key] = value;
          break; // first cookie that yields a value wins
        }
      }
    }
  } catch {
    // tracking must never throw
  }
  return out;
}
